import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Spinner, Pagination, Modal, Dropdown } from 'react-bootstrap';
import { searchHistoricalTrips, getClients, getDrivers, getBases, getAreas, getCostCenters, getVehicleTypes } from '../api';
import * as XLSX from 'xlsx';

const float_val = (v) => parseFloat(v || 0);

const getDriverName = (driver) => {
    if (!driver) return 'Sin chofer';
    if (typeof driver === 'string') return driver;
    if (driver.full_name && driver.full_name.trim()) return driver.full_name.trim();
    const first = driver.first_name || '';
    const last = driver.last_name || '';
    const combined = `${first} ${last}`.trim();
    if (combined && combined !== 'undefined undefined') return combined;
    if (driver.name && driver.name.trim()) return driver.name.trim();
    return 'Sin chofer';
};

const getDriverPayment = (t) => {
    if (!t) return 0;
    const baseCost = float_val(t.driver_earnings) || float_val(t.provider_cost_base) || float_val(t.provider_cost_local) || 0;
    const waitCost = float_val(t.waiting_time_provider_cost) || 0;
    return baseCost + waitCost;
};

const getPassengerCount = (t) => {
    if (!t) return 1;
    if (t.passenger_count && t.passenger_count > 0) return t.passenger_count;
    if (t.additional_passengers && Array.isArray(t.additional_passengers)) {
        return 1 + t.additional_passengers.length;
    }
    return 1;
};

const HistoricalTripSearch = ({ isLoaded }) => {
    // Listas para dropdowns de filtros
    const [clients, setClients] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [bases, setBases] = useState([]);
    const [areas, setAreas] = useState([]);
    const [costCenters, setCostCenters] = useState([]);
    const [vehicleTypes, setVehicleTypes] = useState([]);

    // Filtros
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    // Multi-Select Empresas
    const [selectedClientIds, setSelectedClientIds] = useState([]);
    const [clientSearchTerm, setClientSearchTerm] = useState('');

    const [selectedArea, setSelectedArea] = useState('');
    const [selectedCostCenter, setSelectedCostCenter] = useState('');
    const [selectedVehicleType, setSelectedVehicleType] = useState('');
    const [selectedPaxCount, setSelectedPaxCount] = useState('');
    const [customPaxInput, setCustomPaxInput] = useState('');

    const [passengerName, setPassengerName] = useState('');
    const [addressTerm, setAddressTerm] = useState('');
    const [selectedDriver, setSelectedDriver] = useState('');
    const [vehicleCode, setVehicleCode] = useState('');
    const [externalRef, setExternalRef] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedBase, setSelectedBase] = useState('');

    // Resultados y Paginación
    const [trips, setTrips] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const pageSize = 50;
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Modal de Detalle
    const [selectedTrip, setSelectedTrip] = useState(null);

    // Cargar listas iniciales
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                const [cls, drvs, bs, ars, ccs, vts] = await Promise.all([
                    getClients({ limit: 1000 }),
                    getDrivers({ limit: 1000 }),
                    getBases({ limit: 100 }),
                    getAreas(),
                    getCostCenters(),
                    getVehicleTypes()
                ]);
                const clientList = Array.isArray(cls) ? cls : (cls?.clients || cls?.items || []);
                const driverList = Array.isArray(drvs) ? drvs : (drvs?.drivers || drvs?.items || []);
                const baseList = Array.isArray(bs) ? bs : (bs?.bases || bs?.items || []);
                const areaList = Array.isArray(ars) ? ars : (ars?.areas || ars?.items || []);
                const ccList = Array.isArray(ccs) ? ccs : (ccs?.cost_centers || ccs?.items || []);
                const vtList = Array.isArray(vts) ? vts : (vts?.vehicle_types || vts?.items || []);

                setClients([...clientList].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
                setDrivers([...driverList].sort((a, b) => getDriverName(a).localeCompare(getDriverName(b))));
                setBases([...baseList].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
                setAreas([...areaList].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
                setCostCenters([...ccList].sort((a, b) => (a.name || a.code || '').localeCompare(b.name || b.code || '')));
                setVehicleTypes([...vtList].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
            } catch (err) {
                console.error("Error al cargar datos auxiliares:", err);
            }
        };
        loadInitialData();
        
        // Inicializar fechas por defecto (Últimos 30 días)
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        setEndDate(end.toISOString().split('T')[0]);
        setStartDate(start.toISOString().split('T')[0]);
    }, []);

    // Función principal para realizar la búsqueda en el backend
    const fetchHistoricalTrips = useCallback(async (targetPage = 1) => {
        setLoading(true);
        try {
            let vtParam = selectedVehicleType || undefined;
            if (selectedVehicleType === 'BUS_GROUP') {
                const busTypeIds = vehicleTypes
                    .filter(vt => (vt.name || '').toLowerCase().includes('bus') || (vt.name || '').toLowerCase().includes('sprinter'))
                    .map(vt => vt.id);
                vtParam = busTypeIds.length > 0 ? busTypeIds.join(',') : undefined;
            }

            const paxParam = customPaxInput.trim() || selectedPaxCount || undefined;

            const params = {
                page: targetPage,
                page_size: pageSize,
                start_date: startDate ? `${startDate}T00:00:00` : undefined,
                end_date: endDate ? `${endDate}T23:59:59` : undefined,
                client_id: selectedClientIds.length > 0 ? selectedClientIds.join(',') : undefined,
                area_id: selectedArea || undefined,
                cost_center_id: selectedCostCenter || undefined,
                requested_vehicle_type_id: vtParam,
                passenger_count: paxParam,
                passenger_name: passengerName.trim() || undefined,
                search_term: addressTerm.trim() || undefined,
                driver_id: selectedDriver || undefined,
                vehicle_code: vehicleCode.trim() || undefined,
                external_reference: externalRef.trim() || undefined,
                status: selectedStatus || undefined,
                origin_base_id: selectedBase || undefined
            };

            const data = await searchHistoricalTrips(params);
            setTrips(data.trips || []);
            setTotal(data.total || 0);
            setPage(data.page || targetPage);
        } catch (err) {
            console.error("Error al realizar búsqueda histórica:", err);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, selectedClientIds, selectedArea, selectedCostCenter, selectedVehicleType, vehicleTypes, selectedPaxCount, customPaxInput, passengerName, addressTerm, selectedDriver, vehicleCode, externalRef, selectedStatus, selectedBase, pageSize]);

    // Ejecutar búsqueda al presionar "Buscar" o cambiar paginación
    const handleSearch = (e) => {
        if (e) e.preventDefault();
        setPage(1);
        fetchHistoricalTrips(1);
    };

    const handleClearFilters = () => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        setEndDate(end.toISOString().split('T')[0]);
        setStartDate(start.toISOString().split('T')[0]);
        setSelectedClientIds([]);
        setClientSearchTerm('');
        setSelectedArea('');
        setSelectedCostCenter('');
        setSelectedVehicleType('');
        setSelectedPaxCount('');
        setCustomPaxInput('');
        setPassengerName('');
        setAddressTerm('');
        setSelectedDriver('');
        setVehicleCode('');
        setExternalRef('');
        setSelectedStatus('');
        setSelectedBase('');
        setPage(1);
    };

    // Presets de fechas
    const applyDatePreset = (preset) => {
        const end = new Date();
        let start = new Date();

        if (preset === 'last30') {
            start.setDate(start.getDate() - 30);
        } else if (preset === 'thisMonth') {
            start = new Date(end.getFullYear(), end.getMonth(), 1);
        } else if (preset === 'lastMonth') {
            start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
            end.setDate(0); // Último día del mes anterior
        } else if (preset === 'fullYear') {
            start.setDate(start.getDate() - 365);
        }

        setStartDate(start.toISOString().split('T')[0]);
        setEndDate(end.toISOString().split('T')[0]);
    };

    // Helpers Multi-Select Clientes
    const toggleClientSelection = (clientId) => {
        setSelectedClientIds(prev => 
            prev.includes(clientId) 
                ? prev.filter(id => id !== clientId)
                : [...prev, clientId]
        );
    };

    const handleSelectAllClients = () => {
        setSelectedClientIds(clients.map(c => c.id));
    };

    const handleDeselectAllClients = () => {
        setSelectedClientIds([]);
    };

    // Exportar a Excel los resultados actuales
    const handleExportExcel = async () => {
        setExporting(true);
        try {
            let vtParam = selectedVehicleType || undefined;
            if (selectedVehicleType === 'BUS_GROUP') {
                const busTypeIds = vehicleTypes
                    .filter(vt => (vt.name || '').toLowerCase().includes('bus') || (vt.name || '').toLowerCase().includes('sprinter'))
                    .map(vt => vt.id);
                vtParam = busTypeIds.length > 0 ? busTypeIds.join(',') : undefined;
            }

            const paxParam = customPaxInput.trim() || selectedPaxCount || undefined;

            const params = {
                page: 1,
                page_size: 5000,
                start_date: startDate ? `${startDate}T00:00:00` : undefined,
                end_date: endDate ? `${endDate}T23:59:59` : undefined,
                client_id: selectedClientIds.length > 0 ? selectedClientIds.join(',') : undefined,
                area_id: selectedArea || undefined,
                cost_center_id: selectedCostCenter || undefined,
                requested_vehicle_type_id: vtParam,
                passenger_count: paxParam,
                passenger_name: passengerName.trim() || undefined,
                search_term: addressTerm.trim() || undefined,
                driver_id: selectedDriver || undefined,
                vehicle_code: vehicleCode.trim() || undefined,
                external_reference: externalRef.trim() || undefined,
                status: selectedStatus || undefined,
                origin_base_id: selectedBase || undefined
            };

            const data = await searchHistoricalTrips(params);
            const exportTrips = data.trips || [];

            const excelRows = exportTrips.map(t => ({
                'ID Servicio': t.id,
                'Fecha / Hora': t.scheduled_time ? new Date(t.scheduled_time).toLocaleString('es-CL') : '',
                'Cliente': t.client?.name || '---',
                'Área': t.area?.name || t.area_label || '---',
                'Centro de Costo': t.cost_center?.name || t.cost_center?.code || t.cost_center_label || '---',
                'Pasajero Principal': t.passenger_name || '---',
                'Cant. Pasajeros': getPassengerCount(t),
                'Solicitante': t.requester?.full_name || t.external_solicitante || '---',
                'Origen': t.pickup_address || '---',
                'Destino': t.dropoff_address || '---',
                'Conductor': getDriverName(t.driver),
                'Patente / Móvil': t.vehicle?.internal_code || '---',
                'Tipo Vehículo': t.requested_vehicle_type?.name || '---',
                'Estado': t.status || '---',
                'Folio / Ref': t.external_reference || t.validation_folio || '---',
                'Tarifa Cliente ($)': float_val(t.estimated_price) + float_val(t.waiting_time_amount),
                'Pago Chofer ($)': getDriverPayment(t),
                'Margen ($)': (float_val(t.estimated_price) + float_val(t.waiting_time_amount)) - getDriverPayment(t),
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelRows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Historico_Viajes");
            XLSX.writeFile(workbook, `Historico_Servicios_${startDate}_al_${endDate}.xlsx`);
        } catch (err) {
            console.error("Error al exportar a Excel:", err);
        } finally {
            setExporting(false);
        }
    };

    // Renderizado de badget de estado
    const renderStatusBadge = (status) => {
        const statusMap = {
            completed: { bg: 'success', text: 'Completado' },
            in_progress: { bg: 'info', text: 'En Curso' },
            en_route: { bg: 'primary', text: 'En Camino' },
            assigned: { bg: 'warning', text: 'Asignado' },
            no_show: { bg: 'danger', text: 'No Show' },
            cancelled: { bg: 'secondary', text: 'Cancelado' },
            pending: { bg: 'light', text: 'Pendiente', textColor: 'text-dark' }
        };
        const s = statusMap[status] || { bg: 'secondary', text: status || 'Desconocido' };
        return <Badge bg={s.bg} className={s.textColor}>{s.text}</Badge>;
    };

    const totalPages = Math.ceil(total / pageSize) || 1;

    return (
        <Container fluid className="py-3 px-4">
            {/* Header Módulo */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="fw-bold mb-0 text-dark">
                        🔍 Buscador Histórico Anual de Servicios
                    </h4>
                    <small className="text-muted">
                        Consulta y audita el historial completo de viajes sin límite de 31 días.
                    </small>
                </div>
                <div>
                    <Button 
                        variant="success" 
                        size="sm" 
                        onClick={handleExportExcel} 
                        disabled={exporting || trips.length === 0}
                        className="fw-bold shadow-sm"
                    >
                        {exporting ? <Spinner animation="border" size="sm" /> : '📊 Exportar a Excel'}
                    </Button>
                </div>
            </div>

            {/* Panel de Filtros Dedicados */}
            <Card className="shadow-sm border-0 mb-4 bg-light">
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        {/* Presets de Fechas */}
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <span className="small fw-bold text-secondary me-2">Presets de Fecha:</span>
                            <Button variant="outline-primary" size="sm" onClick={() => applyDatePreset('last30')}>Últimos 30 días</Button>
                            <Button variant="outline-primary" size="sm" onClick={() => applyDatePreset('thisMonth')}>Este Mes</Button>
                            <Button variant="outline-primary" size="sm" onClick={() => applyDatePreset('lastMonth')}>Mes Anterior</Button>
                            <Button variant="outline-primary" size="sm" onClick={() => applyDatePreset('fullYear')}>Año Móvil (365d)</Button>
                        </div>

                        <Row className="g-2">
                            {/* Fechas */}
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Fecha Desde</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    size="sm" 
                                    value={startDate} 
                                    onChange={(e) => setStartDate(e.target.value)} 
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Fecha Hasta</Form.Label>
                                <Form.Control 
                                    type="date" 
                                    size="sm" 
                                    value={endDate} 
                                    onChange={(e) => setEndDate(e.target.value)} 
                                />
                            </Col>
                            
                            {/* Multi-Select Empresa / Cliente con Buscador */}
                            <Col md={4}>
                                <Form.Label className="small fw-bold mb-1">
                                    Empresas / Clientes ({selectedClientIds.length > 0 ? `${selectedClientIds.length} sel.` : 'Todas'})
                                </Form.Label>
                                <Dropdown className="w-100" autoClose="outside">
                                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="w-100 d-flex justify-content-between align-items-center text-truncate bg-white">
                                        <span>
                                            {selectedClientIds.length === 0 
                                                ? "Todas las empresas" 
                                                : selectedClientIds.length === 1 
                                                    ? (clients.find(c => c.id === selectedClientIds[0])?.name || "1 empresa")
                                                    : `${selectedClientIds.length} empresas seleccionadas`}
                                        </span>
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu className="p-2 shadow border-0 w-100" style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                        <Form.Control 
                                            type="text" 
                                            size="sm" 
                                            placeholder="🔍 Buscar empresa..." 
                                            value={clientSearchTerm} 
                                            onChange={(e) => setClientSearchTerm(e.target.value)}
                                            className="mb-2"
                                        />
                                        <div className="d-flex justify-content-between mb-2 pb-1 border-bottom">
                                            <Button variant="link" size="sm" className="p-0 text-decoration-none small" onClick={handleSelectAllClients}>
                                                Seleccionar Todos
                                            </Button>
                                            <Button variant="link" size="sm" className="p-0 text-decoration-none text-danger small" onClick={handleDeselectAllClients}>
                                                Limpiar
                                            </Button>
                                        </div>
                                        {clients
                                            .filter(c => (c.name || '').toLowerCase().includes(clientSearchTerm.toLowerCase()))
                                            .map(c => (
                                                <Form.Check 
                                                    key={c.id} 
                                                    type="checkbox" 
                                                    id={`client-check-${c.id}`} 
                                                    label={c.name} 
                                                    checked={selectedClientIds.includes(c.id)} 
                                                    onChange={() => toggleClientSelection(c.id)}
                                                    className="small py-1 border-bottom"
                                                />
                                            ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                                {selectedClientIds.length > 0 && (
                                    <div className="d-flex flex-wrap gap-1 mt-1" style={{ maxHeight: '60px', overflowY: 'auto' }}>
                                        {selectedClientIds.map(id => {
                                            const clientObj = clients.find(c => c.id === id);
                                            return (
                                                <Badge key={id} bg="primary" className="d-flex align-items-center gap-1 small fw-normal">
                                                    {clientObj?.name || id}
                                                    <span style={{ cursor: 'pointer' }} onClick={() => toggleClientSelection(id)}>×</span>
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                )}
                            </Col>

                            {/* Área */}
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Área</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={selectedArea} 
                                    onChange={(e) => setSelectedArea(e.target.value)}
                                >
                                    <option value="">Todas las áreas</option>
                                    {areas.map(a => (
                                        <option key={a.id} value={a.id}>{a.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>

                            {/* Centro de Costo */}
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Centro de Costo</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={selectedCostCenter} 
                                    onChange={(e) => setSelectedCostCenter(e.target.value)}
                                >
                                    <option value="">Todos los CC</option>
                                    {costCenters.map(cc => (
                                        <option key={cc.id} value={cc.id}>{cc.name ? `${cc.code} - ${cc.name}` : cc.code}</option>
                                    ))}
                                </Form.Select>
                            </Col>

                            {/* Tipo de Vehículo */}
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Tipo Vehículo</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={selectedVehicleType} 
                                    onChange={(e) => setSelectedVehicleType(e.target.value)}
                                >
                                    <option value="">Todos los tipos</option>
                                    <option value="BUS_GROUP">🚌 Buses / Minibuses (Todos los Buses)</option>
                                    {vehicleTypes.map(vt => (
                                        <option key={vt.id} value={vt.id}>{vt.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>

                            {/* Cantidad de Pasajeros */}
                            <Col md={3}>
                                <Form.Label className="small fw-bold mb-1">Cant. Pasajeros (Pax)</Form.Label>
                                <div className="d-flex gap-1">
                                    <Form.Select 
                                        size="sm" 
                                        value={selectedPaxCount} 
                                        onChange={(e) => {
                                            setSelectedPaxCount(e.target.value);
                                            setCustomPaxInput('');
                                        }}
                                        style={{ flex: 1 }}
                                    >
                                        <option value="">Todos</option>
                                        <option value="1">1 Pax</option>
                                        <option value="2">2 Pax</option>
                                        <option value="3-4">3-4 Pax</option>
                                        <option value="5-10">5-10 Pax</option>
                                        <option value="11-20">11-20 Pax</option>
                                        <option value="21-40">21-40 Pax</option>
                                        <option value="41+">41+ Pax (Buses)</option>
                                    </Form.Select>
                                    <Form.Control 
                                        type="text" 
                                        size="sm" 
                                        placeholder="Exacto (ej: 45)" 
                                        value={customPaxInput} 
                                        onChange={(e) => setCustomPaxInput(e.target.value)} 
                                        style={{ width: '100px' }}
                                    />
                                </div>
                            </Col>

                            {/* Pasajero / Solicitante */}
                            <Col md={3}>
                                <Form.Label className="small fw-bold mb-1">Pasajero o Solicitante</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Nombre o RUT..." 
                                    value={passengerName} 
                                    onChange={(e) => setPassengerName(e.target.value)} 
                                />
                            </Col>

                            {/* Origen / Destino */}
                            <Col md={3}>
                                <Form.Label className="small fw-bold mb-1">Origen o Destino</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Comuna, calle o aeropuerto..." 
                                    value={addressTerm} 
                                    onChange={(e) => setAddressTerm(e.target.value)} 
                                />
                            </Col>

                            {/* Conductor */}
                            <Col md={3}>
                                <Form.Label className="small fw-bold mb-1">Conductor / Chofer</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={selectedDriver} 
                                    onChange={(e) => setSelectedDriver(e.target.value)}
                                >
                                    <option value="">Todos los conductores</option>
                                     {drivers.map(d => (
                                        <option key={d.id} value={d.id}>{getDriverName(d)}</option>
                                     ))}
                                </Form.Select>
                            </Col>

                            {/* Patente / Móvil */}
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Patente / Móvil</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Código móvil..." 
                                    value={vehicleCode} 
                                    onChange={(e) => setVehicleCode(e.target.value)} 
                                />
                            </Col>

                            {/* Folio / OS */}
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Folio / Ref / OS</Form.Label>
                                <Form.Control 
                                    type="text" 
                                    size="sm" 
                                    placeholder="Nro. reserva..." 
                                    value={externalRef} 
                                    onChange={(e) => setExternalRef(e.target.value)} 
                                />
                            </Col>

                            {/* Estado */}
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Estado Servicio</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={selectedStatus} 
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    <option value="">Todos los estados</option>
                                    <option value="completed">Completado</option>
                                    <option value="in_progress">En Curso / On Board</option>
                                    <option value="en_route">En Camino</option>
                                    <option value="assigned">Asignado</option>
                                    <option value="no_show">No Show</option>
                                    <option value="cancelled">Cancelado</option>
                                    <option value="pending">Pendiente</option>
                                </Form.Select>
                            </Col>

                            {/* Base / Sucursal */}
                            <Col md={2}>
                                <Form.Label className="small fw-bold mb-1">Base / Sucursal</Form.Label>
                                <Form.Select 
                                    size="sm" 
                                    value={selectedBase} 
                                    onChange={(e) => setSelectedBase(e.target.value)}
                                >
                                    <option value="">Todas las bases</option>
                                    {bases.map(b => (
                                        <option key={b.id} value={b.id}>{b.name}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end gap-2 mt-3">
                            <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                                🧹 Limpiar Filtros
                            </Button>
                            <Button variant="primary" size="sm" type="submit" className="fw-bold px-4">
                                🔍 Buscar
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>

            {/* Tabla de Resultados */}
            <Card className="shadow-sm border-0">
                <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0 text-dark">
                        Resultados Encontrados: <Badge bg="primary">{total}</Badge>
                    </h6>
                    {loading && (
                        <div className="text-primary small d-flex align-items-center gap-2">
                            <Spinner animation="border" size="sm" /> Buscando en la base de datos...
                        </div>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    <Table responsive hover className="align-middle mb-0 text-nowrap" style={{ fontSize: '0.88rem' }}>
                        <thead className="table-light">
                            <tr>
                                <th>ID / Folio</th>
                                <th>Fecha / Hora</th>
                                <th>Empresa</th>
                                <th>Pasajero</th>
                                <th>Solicitante</th>
                                <th>Ruta (Origen ➔ Destino)</th>
                                <th>Móvil / Chofer</th>
                                <th>Estado</th>
                                <th className="text-end">Tarifa</th>
                                <th className="text-end">Pago Chofer</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trips.length === 0 ? (
                                <tr>
                                    <td colSpan="11" className="text-center py-4 text-muted">
                                        {loading ? "Cargando resultados..." : "No se encontraron viajes con los filtros seleccionados."}
                                    </td>
                                </tr>
                            ) : (
                                trips.map(t => (
                                    <tr key={t.id}>
                                        <td className="fw-bold">
                                            #{t.id}
                                            {t.external_reference && (
                                                <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                                                    {t.external_reference}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {t.scheduled_time ? (
                                                <>
                                                    <div>{new Date(t.scheduled_time).toLocaleDateString('es-CL')}</div>
                                                    <div className="text-muted small">{new Date(t.scheduled_time).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}</div>
                                                </>
                                            ) : '---'}
                                        </td>
                                        <td>
                                            <div className="fw-semibold">{t.client?.name || '---'}</div>
                                            {t.area?.name && <div className="text-muted small" style={{ fontSize: '0.75rem' }}>{t.area.name}</div>}
                                        </td>
                                        <td>
                                            <div className="fw-bold">{t.passenger_name || '---'}</div>
                                            <div className="text-muted small">
                                                {t.passenger_phone ? `${t.passenger_phone} • ` : ''}👥 {getPassengerCount(t)} pax
                                            </div>
                                        </td>
                                        <td>
                                            <div className="fw-semibold">{t.requester?.full_name || t.external_solicitante || '---'}</div>
                                        </td>
                                        <td style={{ maxWidth: '280px', whiteSpace: 'normal' }}>
                                            <div className="text-truncate" title={t.pickup_address}>
                                                🟢 {t.pickup_address || '---'}
                                            </div>
                                            <div className="text-truncate text-muted" title={t.dropoff_address}>
                                                🔴 {t.dropoff_address || '---'}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="fw-bold">{t.vehicle?.internal_code || 'Sin móvil'}</div>
                                            <div className="text-muted small">
                                                {getDriverName(t.driver)}
                                            </div>
                                        </td>
                                        <td>{renderStatusBadge(t.status)}</td>
                                        <td className="text-end fw-bold text-success">
                                            ${(float_val(t.estimated_price) + float_val(t.waiting_time_amount)).toLocaleString('es-CL')}
                                        </td>
                                        <td className="text-end fw-bold text-primary">
                                            ${getDriverPayment(t).toLocaleString('es-CL')}
                                        </td>
                                        <td className="text-center">
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                onClick={() => setSelectedTrip(t)}
                                            >
                                                👁️ Ficha
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>

                {/* Footer Paginador */}
                <Card.Footer className="bg-white d-flex justify-content-between align-items-center py-2">
                    <small className="text-muted">
                        Página {page} de {totalPages} ({total} registros en total)
                    </small>

                    <Pagination size="sm" className="mb-0">
                        <Pagination.First onClick={() => fetchHistoricalTrips(1)} disabled={page === 1 || loading} />
                        <Pagination.Prev onClick={() => fetchHistoricalTrips(page - 1)} disabled={page === 1 || loading} />
                        <Pagination.Item active>{page}</Pagination.Item>
                        <Pagination.Next onClick={() => fetchHistoricalTrips(page + 1)} disabled={page >= totalPages || loading} />
                        <Pagination.Last onClick={() => fetchHistoricalTrips(totalPages)} disabled={page >= totalPages || loading} />
                    </Pagination>
                </Card.Footer>
            </Card>

            {/* Modal Ficha Completa del Viaje */}
            {selectedTrip && (
                <Modal show={true} onHide={() => setSelectedTrip(null)} size="lg" centered>
                    <Modal.Header closeButton className="bg-light">
                        <Modal.Title className="h5 fw-bold">
                            Ficha de Servicio #{selectedTrip.id}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ fontSize: '0.9rem' }}>
                        <Row className="g-3">
                            <Col md={6}>
                                <h6 className="fw-bold border-bottom pb-1 text-primary">Información General</h6>
                                <div><strong>Cliente:</strong> {selectedTrip.client?.name || '---'}</div>
                                <div><strong>Área:</strong> {selectedTrip.area?.name || selectedTrip.area_label || '---'}</div>
                                <div><strong>Centro de Costo:</strong> {selectedTrip.cost_center?.name || selectedTrip.cost_center?.code || selectedTrip.cost_center_label || '---'}</div>
                                <div><strong>Pasajero:</strong> {selectedTrip.passenger_name || '---'} <span className="badge bg-light text-dark border">👥 {getPassengerCount(selectedTrip)} pax</span></div>
                                <div><strong>Solicitante:</strong> {selectedTrip.requester?.full_name || selectedTrip.external_solicitante || '---'}</div>
                                <div><strong>Fecha Agendada:</strong> {selectedTrip.scheduled_time ? new Date(selectedTrip.scheduled_time).toLocaleString('es-CL') : '---'}</div>
                                <div><strong>Estado:</strong> {renderStatusBadge(selectedTrip.status)}</div>
                                <div><strong>Folio / Ref Externa:</strong> {selectedTrip.external_reference || selectedTrip.validation_folio || '---'}</div>
                            </Col>

                            <Col md={6}>
                                <h6 className="fw-bold border-bottom pb-1 text-primary">Operación & Vehículo</h6>
                                <div><strong>Conductor:</strong> {getDriverName(selectedTrip.driver)}</div>
                                <div><strong>Patente / Móvil:</strong> {selectedTrip.vehicle?.internal_code || 'Sin asignación'}</div>
                                <div><strong>Tipo Vehículo:</strong> {selectedTrip.requested_vehicle_type?.name || '---'}</div>
                                <div><strong>Tarifa Cliente (Venta):</strong> ${(float_val(selectedTrip.estimated_price) + float_val(selectedTrip.waiting_time_amount)).toLocaleString('es-CL')}</div>
                                <div><strong>Pago Chofer (Costo):</strong> ${getDriverPayment(selectedTrip).toLocaleString('es-CL')}</div>
                                <div><strong>Margen Estimado:</strong> ${((float_val(selectedTrip.estimated_price) + float_val(selectedTrip.waiting_time_amount)) - getDriverPayment(selectedTrip)).toLocaleString('es-CL')}</div>
                            </Col>

                            <Col md={12}>
                                <h6 className="fw-bold border-bottom pb-1 text-primary">Ruta de Servicio</h6>
                                <div><strong>Origen:</strong> {selectedTrip.pickup_address || '---'}</div>
                                <div><strong>Destino:</strong> {selectedTrip.dropoff_address || '---'}</div>
                            </Col>

                            {selectedTrip.internal_notes && (
                                <Col md={12}>
                                    <h6 className="fw-bold border-bottom pb-1 text-primary">Observaciones / Notas</h6>
                                    <div className="bg-light p-2 rounded text-muted">{selectedTrip.internal_notes}</div>
                                </Col>
                            )}
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" size="sm" onClick={() => setSelectedTrip(null)}>
                            Cerrar
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Container>
    );
};

export default HistoricalTripSearch;
